import { FiCheckCircle } from 'react-icons/fi';

export const presentPerfectIntroData = {
  courseId: 'present-perfect-intro',
  courseType: 'grammar',
  title: 'Present Perfect Introduction',
  icon: FiCheckCircle,
  overview: {
    impact: 'Connects past to present — critical for IELTS accuracy',
    focus: 'have/has + past participle',
    uses: 'Speaking Parts 1-3, Writing Task 2',
    duration: '25 minutes'
  },
  sections: [
    {
      title: 'Formation',
      content: `<p>Subject + <strong>have/has</strong> + <strong>past participle</strong></p><p>I/You/We/They <strong>have worked</strong>. He/She/It <strong>has worked</strong>.</p>`,
      examples: [
        {
          label: 'Experience',
          text: 'I have visited Japan twice.'
        },
        {
          label: 'Result now',
          text: 'She has lost her keys. (She can\'t get in now.)'
        },
        {
          label: 'Unfinished',
          text: 'I have lived here since 2020.'
        }
      ]
    },
    {
      title: 'Key Uses',
      content: `<p>The present perfect has three main uses:</p>`,
      points: [
        'Life experience (ever/never): \'Have you ever tried sushi?\'',
        'Recent result affecting now (just/already/yet): \'I\'ve just finished.\'',
        'Unfinished time period (since/for): \'I\'ve worked here for 5 years.\''
      ]
    },
    {
      title: 'Present Perfect vs Past Simple',
      content: `<p>The most tested grammar point in IELTS:</p>`,
      table: {
        headers: [
          'Present Perfect',
          'Past Simple'
        ],
        rows: [
          [
            'No specific time: \'I\'ve been to London.\'',
            'Specific time: \'I went to London in 2022.\''
          ],
          [
            'Unfinished time: \'I\'ve read 3 books this month.\'',
            'Finished time: \'I read 3 books last month.\''
          ],
          [
            'Result matters now: \'I\'ve lost my phone.\'',
            'Just reporting: \'I lost my phone yesterday.\''
          ]
        ]
      }
    }
  ],
  exercises: [
    {
      title: 'Present Perfect or Past Simple?',
      type: 'select',
      instructions: 'Choose the correct tense.',
      questions: [
        {
          text: 'I ___ (visit) Paris three times.',
          options: [
            'have visited',
            'visited',
            'am visiting'
          ],
          answer: 'have visited',
          explanation: 'Life experience, no specific time → present perfect.'
        },
        {
          text: 'She ___ (move) to London in 2019.',
          options: [
            'moved',
            'has moved',
            'was moving'
          ],
          answer: 'moved',
          explanation: 'Specific past time (in 2019) → past simple.'
        },
        {
          text: 'They ___ (not finish) the project yet.',
          options: [
            'haven\'t finished',
            'didn\'t finish',
            'aren\'t finishing'
          ],
          answer: 'haven\'t finished',
          explanation: '\'Yet\' signals present perfect.'
        },
        {
          text: 'We ___ (see) that film last night.',
          options: [
            'saw',
            'have seen',
            'were seeing'
          ],
          answer: 'saw',
          explanation: 'Specific past time (last night) → past simple.'
        }
      ]
    }
  ],
  tips: [
    'Key words for present perfect: ever, never, already, yet, just, since, for',
    'Key words for past simple: yesterday, ago, last week, in 2020',
    'Don\'t say \'I have visited London last year\' — use past simple with \'last year\'',
    'Practice: \'I have lived here FOR 5 years / SINCE 2019\''
  ],
  dailyPractice: [
    'Practice present perfect introduction concepts daily',
    'Review and apply in IELTS practice tests',
    'Write example sentences using key concepts',
    'Teach the concept to someone else to reinforce understanding'
  ],
  backPath: '/exams/english/#grammar',
  backLabel: 'Back to Grammar',
  aiContext: 'Present Perfect: have/has + past participle. Links past to present. Used for experience, recent results, unfinished time periods. Key contrast with past simple for IELTS.'
};

export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...presentPerfectIntroData,
      title: presentPerfectIntroData.title + ' (Foundations)',
      overview: { ...presentPerfectIntroData.overview, duration: '15-20 minutes' },
      sections: presentPerfectIntroData.sections.slice(0, Math.max(2, Math.ceil(presentPerfectIntroData.sections.length / 2))),
      exercises: presentPerfectIntroData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...presentPerfectIntroData,
      title: presentPerfectIntroData.title + ' (Advanced)',
      overview: { ...presentPerfectIntroData.overview, duration: '30-35 minutes' },
      sections: presentPerfectIntroData.sections,
      exercises: presentPerfectIntroData.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(presentPerfectIntroData.tips || []),
        'For Band 8+: Focus on natural-sounding usage rather than just accuracy.',
        'Advanced tip: Integrate this concept fluidly in timed IELTS tasks.',
      ],
    };
  }
  return presentPerfectIntroData;
}
